import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Header, Segment, Divider, List, Loader, Button, Message } from "semantic-ui-react";
import { format } from "date-fns";
import { closeModal } from "../../../../app/common/modals/modalSlice";
import ModalWrapper from "../../../../app/common/modals/modalWrapper";
import { functionGetStrategyGenerationResult } from "../../../../app/firestore/functionsService";
import StrategyFeedback from "./StrategyFeedback";
import { buildListingDescriptionFeedbackConfig } from "./listingDescriptionFeedbackConfig";

// A Firestore Timestamp returned through a Cloud Functions onCall response
// serializes as a plain {_seconds, _nanoseconds}-shaped object with no
// .toDate() method (not the client SDK's Timestamp class) - new Date() on
// that produces an Invalid Date, and date-fns' format() throws a hard
// RangeError on it rather than failing quietly. Guard before formatting.
function toDisplayDate(value) {
  if (!value) return null;
  const date = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : format(date, "MMM d, yyyy");
}

// Read-only viewer for a PREVIOUSLY generated Listing Description Strategy
// on a property that is no longer the transaction's current address - sibling
// to StrategyResultViewerModal.jsx rather than a strategyType branch inside
// it, since the content shape (title/MLS/social/extended + 3-category
// compliance review) is genuinely different from Pricing's.
export default function StrategyListingDescriptionResultViewerModal({ transactionId, propertyKey, propertyAddress, generationId, agentRepresents }) {
  const dispatch = useDispatch();
  const [generation, setGeneration] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    functionGetStrategyGenerationResult({ transactionId, generationId })
      .then((data) => {
        if (!cancelled) setGeneration(data);
      })
      .catch(() => {
        if (!cancelled) setGeneration(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [transactionId, generationId]);

  function handleClose() {
    dispatch(closeModal({ modalType: "StrategyListingDescriptionResultViewerModal" }));
  }

  const result = generation?.result;
  const userEdits = generation?.userEdits || {};

  return (
    <ModalWrapper size="large" header="Previous Property Listing Description">
      <Segment clearing>
        <p className="text-medium">
          <b>{propertyAddress?.street}</b>
          {propertyAddress?.unit ? ` ${propertyAddress.unit}` : ""}
          <br />
          {propertyAddress?.city}, {propertyAddress?.state}
        </p>
        {toDisplayDate(generation?.createdAt) && (
          <p className="small top margin text-grey" style={{ fontSize: "0.85em" }}>
            Generated {toDisplayDate(generation.createdAt)}
          </p>
        )}
        <Divider />
        {loading && <Loader active inline="centered" content="Loading..." />}
        {!loading && !result && <p className="text-grey">No saved result found for this property.</p>}
        {!loading && result && (
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div>
              <Header as="h3" className="zero bottom margin">{userEdits.title ?? result.title}</Header>
            </div>

            <div>
              <Header as="h4">MLS Description</Header>
              <p style={{ whiteSpace: "pre-wrap" }}>{userEdits.mlsDescription ?? result.mlsDescription}</p>
            </div>

            <div>
              <Header as="h4">Social Media Caption</Header>
              <p style={{ whiteSpace: "pre-wrap" }}>{userEdits.socialCaption ?? result.socialCaption}</p>
            </div>

            <div>
              <Header as="h4">Extended Description</Header>
              <p style={{ whiteSpace: "pre-wrap" }}>{userEdits.extendedDescription ?? result.extendedDescription}</p>
            </div>

            {(result.fairHousingReview?.items?.length > 0 || result.accuracyAdvertisingReview?.length > 0) && (
              <div>
                <Header as="h4">Fair Housing & Advertising Review</Header>
                <List divided relaxed>
                  {(result.fairHousingReview?.items || []).map((item, idx) => (
                    <List.Item key={`fh-${idx}`}>
                      <List.Content>
                        <List.Header>"{item.originalText}"</List.Header>
                        <List.Description>{item.concern}</List.Description>
                      </List.Content>
                    </List.Item>
                  ))}
                  {(result.accuracyAdvertisingReview || []).map((item, idx) => (
                    <List.Item key={`aa-${idx}`}>
                      <List.Content>
                        <List.Header>"{item.originalText}"</List.Header>
                        <List.Description>{item.concern}</List.Description>
                      </List.Content>
                    </List.Item>
                  ))}
                </List>
              </div>
            )}

            {(!result.fairHousingReview?.items || result.fairHousingReview.items.length === 0) &&
              (!result.accuracyAdvertisingReview || result.accuracyAdvertisingReview.length === 0) && (
                <Message positive size="small">No potential Fair Housing advertising concerns detected.</Message>
              )}

            <Divider />
            <StrategyFeedback
              transactionId={transactionId}
              propertyKey={propertyKey}
              propertyAddress={propertyAddress}
              strategyType="listingDescription"
              generationId={generationId}
              generatedRecommendationSnapshot={{ title: result.title }}
              engineMetadata={{ modelName: generation.modelName || null }}
              config={buildListingDescriptionFeedbackConfig()}
            />
          </div>
        )}
        <Divider className="medium top margin" />
        <Button onClick={handleClose}>Close</Button>
      </Segment>
    </ModalWrapper>
  );
}
